import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BlogPost } from '../types/BlogPost';
import { getBlogPostBySlug } from '../services/blogService';
import { trackBlogPostView } from '../services/analyticsService';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [blogPost, setBlogPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (slug) {
        loadBlogPost(slug);
      }
    } catch (err) {
      console.error('Error in blog post page useEffect:', err);
    }
  }, [slug]);

  const loadBlogPost = async (postSlug: string) => {
    try {
      setLoading(true);
      setError(null);
      const post = await getBlogPostBySlug(postSlug);
      
      if (post) {
        setBlogPost(post);
        
        // Set SEO meta tags
        document.title = `${post.title} | Cheapest Car Recovery UK Blog`;
        
        const metaTags = [
          { name: 'description', content: post.description },
          { name: 'keywords', content: post.metaKeywords },
          { name: 'author', content: post.author },
          { property: 'og:title', content: post.title },
          { property: 'og:description', content: post.description },
          { property: 'og:type', content: 'article' },
          { property: 'og:url', content: `https://cheapestcarrecovery.uk/blog/${post.slug}` },
          { property: 'og:image', content: post.imageUrl },
          { property: 'article:author', content: post.author },
          { property: 'article:published_time', content: post.publishedAt },
          { property: 'article:modified_time', content: post.updatedAt }
        ];

        metaTags.forEach(tag => {
          const existingTag = document.querySelector(`meta[name="${tag.name}"], meta[property="${tag.property}"]`);
          if (existingTag) {
            existingTag.remove();
          }
          
          const metaTag = document.createElement('meta');
          if (tag.name) metaTag.setAttribute('name', tag.name);
          if (tag.property) metaTag.setAttribute('property', tag.property);
          metaTag.setAttribute('content', tag.content);
          document.head.appendChild(metaTag);
        });

        // Add structured data for article
        const structuredData = {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.description,
          "image": post.imageUrl,
          "author": {
            "@type": "Organization",
            "name": post.author
          },
          "publisher": {
            "@type": "Organization",
            "name": "Cheapest Car Recovery UK",
            "logo": {
              "@type": "ImageObject",
              "url": "https://cheapestcarrecovery.uk/my-logo.png"
            }
          },
          "datePublished": post.publishedAt,
          "dateModified": post.updatedAt,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://cheapestcarrecovery.uk/blog/${post.slug}`
          }
        };

        const existingScript = document.querySelector('script[type="application/ld+json"]');
        if (existingScript) {
          existingScript.remove();
        }
        
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(structuredData);
        document.head.appendChild(script);

        // Track blog post view
        trackBlogPostView(post.id!, post.slug);
        
      } else {
        setError('Blog post not found');
      }
    } catch (err) {
      console.error('Error loading blog post:', err);
      setError('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const responsiveStyles = `
    @media (max-width: 768px) {
      .blog-content {
        padding: 20px !important;
      }
      .blog-title {
        font-size: 28px !important;
      }
    }
  `;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: '18px' }}>Loading blog post...</div>
        </div>
      </div>
    );
  }

  if (error || !blogPost) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: '18px', marginBottom: '20px' }}>
            {error || 'Blog post not found'}
          </div>
          <Link to="/blog" style={{
            backgroundColor: '#FFF222',
            color: '#333',
            padding: '12px 24px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: 'bold'
          }}>
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{responsiveStyles}</style>
      <div style={{ fontFamily: 'Arial, sans-serif', minHeight: '100vh' }}>
        {/* Header */}
        <header style={{
          backgroundColor: '#333',
          color: 'white',
          padding: '20px 0'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Link to="/" style={{ 
                color: 'white', 
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <img 
                  src="/my-logo.png" 
                  alt="Cheapest Car Recovery UK Logo" 
                  style={{ height: '35px', width: 'auto' }}
                />
                <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                  CHEAPEST CAR RECOVERY UK
                </span>
              </Link>
              <div style={{ display: 'flex', gap: '15px' }}>
                <Link to="/blog" style={{
                  color: 'white',
                  textDecoration: 'none',
                  padding: '8px 16px'
                }}>
                  Blog
                </Link>
                <Link to="/" style={{
                  backgroundColor: '#FFF222',
                  color: '#333',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  textDecoration: 'none',
                  fontWeight: 'bold'
                }}>
                  Get Quote
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Blog Post Content */}
        <article style={{ backgroundColor: 'white' }}>
          {/* Hero Image */}
          {blogPost.imageUrl && (
            <div style={{
              height: '400px',
              backgroundImage: `url(${blogPost.imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                right: '20px',
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '20px',
                borderRadius: '8px'
              }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                  <Link to="/blog" style={{
                    color: '#FFF222',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    ← Back to Blog
                  </Link>
                  {blogPost.featured && (
                    <span style={{
                      backgroundColor: '#FFF222',
                      color: '#333',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      marginLeft: '15px'
                    }}>
                      FEATURED
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="blog-content" style={{
            maxWidth: '800px',
            margin: '0 auto',
            padding: '40px 20px'
          }}>
            {/* Title and Meta */}
            <header style={{ marginBottom: '30px' }}>
              <h1 className="blog-title" style={{
                fontSize: '36px',
                fontWeight: 'bold',
                color: '#333',
                marginBottom: '20px',
                lineHeight: '1.2'
              }}>
                {blogPost.title}
              </h1>

              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '15px',
                fontSize: '14px',
                color: '#666',
                marginBottom: '20px'
              }}>
                <span>By {blogPost.author}</span>
                <span>•</span>
                <span>{new Date(blogPost.publishedAt).toLocaleDateString()}</span>
                <span>•</span>
                <span>{blogPost.readingTime} min read</span>
              </div>

              {blogPost.tags.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  {blogPost.tags.map((tag, index) => (
                    <span key={index} style={{
                      backgroundColor: '#f8f9fa',
                      color: '#495057',
                      padding: '4px 12px',
                      borderRadius: '15px',
                      fontSize: '12px',
                      border: '1px solid #dee2e6'
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {/* Content */}
            <div style={{
              fontSize: '16px',
              lineHeight: '1.8',
              color: '#333'
            }}>
              {blogPost.content.split('\n').map((paragraph, index) => (
                <p key={index} style={{ marginBottom: '20px' }}>
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Updated info */}
            {blogPost.updatedAt !== blogPost.publishedAt && (
              <div style={{
                marginTop: '40px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                fontSize: '14px',
                color: '#666'
              }}>
                Last updated: {new Date(blogPost.updatedAt).toLocaleDateString()}
              </div>
            )}
          </div>
        </article>

        {/* Call to Action */}
        <section style={{
          backgroundColor: '#FFF222',
          padding: '40px 20px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '24px', marginBottom: '15px', color: '#333' }}>
              Need Car Recovery Services?
            </h2>
            <p style={{ fontSize: '16px', marginBottom: '25px', color: '#333' }}>
              Professional 24/7 car recovery across the UK at the most affordable rates
            </p>
            <Link to="/" style={{
              backgroundColor: '#333',
              color: 'white',
              padding: '15px 30px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '16px',
              fontWeight: 'bold'
            }}>
              Get Instant Quote
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer style={{
          backgroundColor: '#333',
          color: 'white',
          padding: '40px 20px',
          textAlign: 'center'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '15px' }}>
              <img 
                src="/my-logo.png" 
                alt="Cheapest Car Recovery UK Logo" 
                style={{ height: '35px', width: 'auto' }}
              />
              <h3 style={{ fontSize: '20px', margin: 0 }}>
                Cheapest Car Recovery UK
              </h3>
            </div>
            <p style={{ fontSize: '14px', opacity: 0.8, marginBottom: '20px' }}>
              Professional car recovery services across the UK • Available 24/7
            </p>
            <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
              <div>📧 admin@cheapestcarrecovery.uk</div>
              <div>📞 +44 7432 387241</div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPostPage; 